// AgentShell.tsx - Marco del portal comercial (agentes) con el diseño ITOBOX Platform 2.0.
// Menú lateral blanco con el logo de la empresa; en celular, menú horizontal bajo el
// encabezado. Las pantallas del agente conservan su lógica: la capa agentTheme.css
// les da el estilo (tarjetas, botones, tablas y etiquetas).
import React, { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, KanbanSquare, Calculator, Wallet, BarChart3, LogOut } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../shared/hooks/useAuth';
import AccountStatusNotice from '../AccountStatusNotice';
import InstallAppBanner from '../InstallAppBanner';
import { useBrandColor } from '../courier/CourierShell';
import '../platformTheme.css';

const MENU = [
    { to: '/agent/dashboard', label: 'Panel comercial', short: 'Panel', icon: LayoutDashboard },
    { to: '/agent/clients', label: 'Mis clientes', short: 'Clientes', icon: Users },
    { to: '/agent/crm', label: 'CRM', short: 'CRM', icon: KanbanSquare },
    { to: '/agent/quotes', label: 'Cotizaciones', short: 'Cotizar', icon: Calculator },
    { to: '/agent/commissions', label: 'Mis comisiones', short: 'Comisiones', icon: Wallet },
    { to: '/agent/reports', label: 'Reportes', short: 'Reportes', icon: BarChart3 },
];

const AgentShell: React.FC<{ children: ReactNode }> = ({ children }) => {
    const brand = useBrandColor();
    const { tenant } = useTenant();
    const { userData, logout } = useAuth();
    const navigate = useNavigate();
    const name = userData?.name || userData?.email?.split('@')[0] || 'Agente';
    const initials = name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
    const logo = tenant?.branding?.logo_url;
    const company = tenant?.branding?.company_name || 'Courier';

    const handleLogout = async () => {
        try { await logout(); } finally { navigate('/login'); }
    };

    return (
        <div className="p2 min-h-screen bg-[#F5F6F8] lg:flex" style={{ ['--p2-brand' as any]: brand }}>
            {/* Menú lateral (computadora) */}
            <aside className="hidden lg:flex w-64 flex-shrink-0 bg-white border-r border-[#EEF0F3] flex-col p-4 gap-1 sticky top-0 h-screen">
                <div className="px-3 pb-6 pt-2">
                    {logo ? <img src={logo} alt={company} className="h-16 w-auto object-contain" /> : <div className="text-xl font-extrabold">{company}</div>}
                    <div className="text-xs text-[#6B7280] mt-1">Portal comercial</div>
                </div>
                {MENU.map(({ to, label, icon: Icon }) => (
                    <NavLink key={to} to={to} style={{ textDecoration: 'none' }}>
                        {({ isActive }) => (
                            <span className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm"
                                style={isActive ? { background: `${brand}14`, color: brand, fontWeight: 600 } : { color: '#6B7280', fontWeight: 500 }}>
                                <Icon size={18} /> {label}
                            </span>
                        )}
                    </NavLink>
                ))}
                <div className="mt-auto flex items-center gap-2.5 p-3 rounded-2xl bg-[#F5F6F8]">
                    <div className="w-9 h-9 rounded-full bg-[#F6C27A] flex items-center justify-center font-bold text-sm text-[#111111]">{initials}</div>
                    <div className="flex-1 min-w-0">
                        <b className="block text-sm truncate text-[#111111]">{name}</b>
                        <span className="text-xs text-[#6B7280]">Agente comercial</span>
                    </div>
                    <button onClick={handleLogout} aria-label="Cerrar sesión" className="p-1.5 text-[#6B7280]"><LogOut size={16} /></button>
                </div>
            </aside>

            <div className="flex-1 min-w-0 flex flex-col">
                {/* Encabezado y menú (celular) */}
                <div className="lg:hidden bg-white border-b border-[#EEF0F3] px-4 pt-4 pb-2" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
                    <div className="flex items-center justify-between">
                        {logo ? <img src={logo} alt={company} className="h-10 w-auto object-contain" /> : <b>{company}</b>}
                        <button onClick={handleLogout} aria-label="Cerrar sesión" className="p-2 text-[#6B7280]"><LogOut size={18} /></button>
                    </div>
                    <nav className="flex gap-2 overflow-x-auto mt-3 pb-1">
                        {MENU.map(({ to, short }) => (
                            <NavLink key={to} to={to} style={{ textDecoration: 'none' }}>
                                {({ isActive }) => (
                                    <span className="block px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border"
                                        style={isActive ? { background: brand, borderColor: brand, color: '#FFFFFF' } : { borderColor: '#E5E7EB', color: '#374151' }}>
                                        {short}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                </div>

                <main className="flex-1 w-full max-w-6xl mx-auto px-4 py-5 lg:px-8 lg:py-8">
                    <AccountStatusNotice />
                    <InstallAppBanner className="mb-4" />
                    {children}
                </main>
            </div>
        </div>
    );
};

export default AgentShell;
